<script>
import confirmationModal from "../../components/modals/modal-confirmation.vue";

const INTEGRATION_KEY = "zoho";
// Private install link for the staging validation build; becomes the Zoho
// Marketplace listing URL at GA.
const ZOHO_INSTALL_URL = "https://crm.zoho.com/market/installPrivatePlugin.do?portalName=danielnoel&nameSpace=ashore&versionIndex=4598191000000304026";

export default {
  name: "zohoIntegration",
  components: {
    confirmationModal,
  },
  data() {
    return {
      loaded: false,
      card: null,
      settings: {
        intakeMode: "zoho",
        emailMode: "ashore_whitelabel",
        embedMode: "whitelabel_link",
        jobReferenceKey: "zohoJobId",
      },
      hooks: [],
      saving: false,
      installUrl: ZOHO_INSTALL_URL,
    };
  },
  computed: {
    connected() {
      return _.get(this.card, "connected", false);
    },
    connectedSince() {
      const at = _.get(this.card, "connectedAt", null);
      return at ? new Date(at).toLocaleDateString() : "";
    },
    // Integrations are premium-and-up; the API enforces this on authorize/refresh,
    // this mirrors it so the card explains itself instead of failing quietly.
    isPremiumPlan() {
      const sku = _.get(this.$A, "Auth.account.subscriptionState.sku", "") || "";
      const tier = sku.split("-")[0].toLowerCase();
      return tier === "premium" || tier === "enterprise";
    },
  },
  mounted: async function () {
    if (this.isPremiumPlan) {
      await this.load();
    } else {
      this.loaded = true;
    }
  },
  methods: {
    load: async function () {
      try {
        const response = await this.$A.IntegrationService.Get(INTEGRATION_KEY);
        if (response && response.data) {
          this.card = response.data;
          this.settings = Object.assign({}, this.settings, response.data.settings || {});
        }
      } catch (e) {
        // Endpoint not deployed yet or transient failure — card stays in its empty state.
      }
      try {
        const health = await this.$A.IntegrationService.GetWebHookHealth();
        if (health && Array.isArray(health.data)) {
          this.hooks = health.data;
        }
      } catch (e) {
        this.hooks = [];
      }
      this.loaded = true;
    },
    async saveSettings() {
      if (this.saving) {
        return;
      }
      this.saving = true;
      try {
        const response = await this.$A.IntegrationService.SaveSettings(
          INTEGRATION_KEY,
          this.settings
        );
        if (response && response.data) {
          this.card = response.data;
          this.settings = Object.assign({}, this.settings, response.data.settings || {});
        }
        this.$A.AlertUser("Zoho Integration Settings Saved", "success");
      } catch (e) {
        const message = _.get(e, "response.data.message", "Could not save settings.");
        this.$A.AlertUser(message, "warning");
      }
      this.saving = false;
    },
    confirmDisconnect() {
      this.$bvModal.show("disconnectZohoIntegration");
    },
    async actuallyDisconnect() {
      try {
        await this.$A.IntegrationService.Disconnect(INTEGRATION_KEY);
        this.$A.AlertUser("Zoho Disconnected", "success");
      } catch (e) {
        this.$A.AlertUser("Could not disconnect Zoho.", "warning");
      }
      await this.load();
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString() : "—";
    },
  },
};
</script>

<template>
  <div class="settings-card">
    <div class="card-head zoho-head">
      <div>
        <div class="card-head-title">Zoho Integration</div>
        <div class="card-head-subtitle">
          Drive Ashore proofing from Zoho CRM and Creator — proofs, approvers, and
          status updates flow both ways.
        </div>
      </div>
      <span class="status-pill" :class="connected ? 'status-on' : 'status-off'">
        <span class="status-dot"></span>
        {{ connected ? "Connected" : "Not connected" }}
      </span>
    </div>

    <!-- Premium gate: the API refuses non-premium grants; say so up front -->
    <div v-if="loaded && !isPremiumPlan" class="list-empty">
      <div class="empty-title">Zoho integration requires the Premium plan</div>
      <div class="empty-text">
        One-click connect, pre-built proofing actions, and automatic webhooks are
        included with Premium and Enterprise plans. Upgrade to switch it on.
      </div>
      <a class="primary-btn empty-cta" href="/billing">View Plans</a>
    </div>

    <!-- Not connected: the panel itself is the setup doc -->
    <div v-if="loaded && isPremiumPlan && !connected" class="list-empty">
      <div class="empty-icon">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 7 L9 3" />
          <path d="M15 7 L15 3" />
          <path d="M6 7h12v4a6 6 0 0 1-12 0V7z" />
          <path d="M12 17v4" />
        </svg>
      </div>
      <div class="empty-title">Connect Ashore to your Zoho account</div>
      <div class="empty-text">
        Install the <b>Ashore Online Proofing</b> extension in Zoho and click
        <b>Authorize</b> when prompted — it connects with one click, registers your
        webhooks automatically, and this panel lights up with your connection status
        and settings.
      </div>
      <a class="primary-btn empty-cta" :href="installUrl" target="_blank" rel="noopener">Add Connection</a>
    </div>

    <div v-if="loaded && isPremiumPlan && connected" class="card-body">
      <div class="connected-meta">
        Connected since <b>{{ connectedSince }}</b>. Ashore accepts proof, contact,
        and workflow calls from your Zoho account, and pushes status events back.
      </div>

      <div class="field">
        <label class="field-label" for="zohoIntakeMode">Artwork intake</label>
        <p class="field-help">Where customer files enter the proofing flow.</p>
        <div class="select-wrap">
          <select id="zohoIntakeMode" class="select-input" v-model="settings.intakeMode">
            <option value="zoho">Zoho sends files to Ashore (portal / CRM uploads)</option>
            <option value="ashore_art_forms">Ashore art intake forms</option>
            <option value="both">Both</option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="field-label" for="zohoEmailMode">Customer email</label>
        <p class="field-help">Who sends proof notifications to your customers.</p>
        <div class="select-wrap">
          <select id="zohoEmailMode" class="select-input" v-model="settings.emailMode">
            <option value="ashore_whitelabel">Ashore sends white-labeled email</option>
            <option value="zoho_owned">Zoho owns all customer email</option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="field-label" for="zohoEmbedMode">Review experience</label>
        <p class="field-help">How customers open proofs from your Zoho portal.</p>
        <div class="select-wrap">
          <select id="zohoEmbedMode" class="select-input" v-model="settings.embedMode">
            <option value="whitelabel_link">White-label review links</option>
            <option value="iframe">Embedded iframe inside the portal</option>
          </select>
        </div>
      </div>

      <div class="field">
        <label class="field-label" for="zohoJobKey">Job reference field</label>
        <p class="field-help">
          The key inside each proof's external metadata that carries your Zoho record
          id — webhook payloads echo it so Zoho can match events to the right job.
        </p>
        <input id="zohoJobKey" type="text" class="text-input" v-model="settings.jobReferenceKey" />
      </div>

      <div class="field" v-if="hooks.length > 0">
        <label class="field-label">Webhook health — last 7 days</label>
        <div class="hook-rows">
          <div class="hook-row hook-row-head">
            <span class="hook-col-name">Webhook</span>
            <span class="hook-col-num">Delivered</span>
            <span class="hook-col-num">Failed</span>
            <span class="hook-col-time">Last delivery</span>
          </div>
          <div class="hook-row" v-for="hook in hooks" :key="hook.webHookId">
            <span class="hook-col-name hook-name">
              <span class="hook-dot" :class="hook.active ? 'hook-dot-on' : 'hook-dot-off'"></span>
              {{ hook.name || hook.url }}
            </span>
            <span class="hook-col-num">{{ hook.deliveredLast7Days }}</span>
            <span class="hook-col-num" :class="{ 'hook-failed': hook.failedLast7Days > 0 }">{{ hook.failedLast7Days }}</span>
            <span class="hook-col-time">{{ formatDate(hook.lastDeliveredAt) }}</span>
          </div>
        </div>
      </div>
    </div>

    <confirmationModal
        v-on:confirmed="actuallyDisconnect"
        ref="confirmDisconnect"
        name="disconnectZohoIntegration"
        confirm-button-text="Disconnect Zoho"
        question="Are you sure you would like to disconnect Zoho?"
        body="Zoho's access tokens will be revoked immediately. Your settings are kept, and re-authorizing from Zoho restores the connection."
    />

    <div class="card-foot" v-if="loaded && isPremiumPlan && connected">
      <a class="secondary-btn" @click="confirmDisconnect">Disconnect</a>
      <a class="primary-btn" @click="saveSettings">Save Settings</a>
    </div>
  </div>
</template>

<style scoped>
.settings-card {
  background: #fff;
  border: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.04), 0 8px 24px -16px rgba(8, 11, 13, 0.1);
}
.card-head {
  padding: 14px 20px;
  background: #fbfbfc;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 12px 12px 0 0;
}
.zoho-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}
.card-head-title {
  font-size: 15px;
  font-weight: 600;
  color: #111827;
}
.card-head-subtitle {
  margin-top: 1px;
  font-size: 12.5px;
  font-weight: 400;
  color: #6b7280;
}
.card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 20px;
}
.card-foot {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  background: #fbfbfc;
  border-top: 1px solid rgba(15, 23, 42, 0.06);
  border-radius: 0 0 12px 12px;
}
.connected-meta {
  font-size: 12.5px;
  color: #4b5563;
  line-height: 1.55;
}
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  height: 24px;
  padding: 0 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}
.status-on {
  background: rgba(1, 146, 86, 0.1);
  color: #019256;
}
.status-off {
  background: rgba(15, 23, 42, 0.06);
  color: #6b7280;
}
.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 999px;
  background: currentColor;
}
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.field-help {
  margin: -2px 0 6px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.text-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13.5px;
  color: #111827;
  background: #fff;
}
.text-input:focus {
  outline: none;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-wrap {
  position: relative;
}
.select-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13.5px;
  color: #111827;
  background: #fff;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
}
.select-input:focus {
  outline: none;
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.select-wrap::after {
  content: "";
  position: absolute;
  right: 13px;
  top: 50%;
  width: 7px;
  height: 7px;
  border-right: 2px solid #6b7280;
  border-bottom: 2px solid #6b7280;
  transform: translateY(-70%) rotate(45deg);
  pointer-events: none;
}
/* Webhook health rows */
.hook-rows {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  overflow: hidden;
}
.hook-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 10px 14px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.hook-row:last-child {
  border-bottom: 0;
}
.hook-row-head {
  padding: 7px 14px;
  background: #fbfbfc;
}
.hook-row-head span {
  font-size: 10.5px;
  font-weight: 700;
  color: #6b7280;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.hook-col-name {
  flex: 1;
  min-width: 0;
}
.hook-name {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #111827;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.hook-col-num {
  width: 70px;
  flex-shrink: 0;
  font-size: 12.5px;
  color: #4b5563;
  text-align: right;
}
.hook-failed {
  color: #b43643;
  font-weight: 700;
}
.hook-col-time {
  width: 170px;
  flex-shrink: 0;
  font-size: 12px;
  color: #6b7280;
  text-align: right;
}
.hook-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
}
.hook-dot-on {
  background: #019256;
}
.hook-dot-off {
  background: #9ca3af;
}
/* Empty state */
.list-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 32px;
}
.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 999px;
  background: rgba(1, 146, 86, 0.1);
  color: #019256;
  margin-bottom: 14px;
}
.empty-title {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  letter-spacing: -0.01em;
}
.empty-text {
  margin-top: 5px;
  font-size: 12.5px;
  font-weight: 500;
  color: #6b7280;
  line-height: 1.55;
  max-width: 420px;
}
/* Buttons */
.primary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 18px;
  background: #019256;
  color: #fff;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
  color: #fff;
  text-decoration: none;
}
.secondary-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  padding: 0 14px;
  background: #fff !important;
  color: #1f2937 !important;
  border: 1px solid rgba(15, 23, 42, 0.12) !important;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 120ms ease, color 120ms ease;
}
.secondary-btn:hover,
.secondary-btn:focus {
  border-color: #b43643 !important;
  color: #b43643 !important;
  text-decoration: none;
}
@media (max-width: 767px) {
  .zoho-head {
    flex-wrap: wrap;
  }
  .hook-col-time {
    display: none;
  }
  .primary-btn,
  .secondary-btn {
    flex: 1;
  }
}
</style>
